<template>
  <div data-private class="h-full flex flex-col">
  <PageHeader
    title="Care Management"
    :heading-level="2"
  >
    <template #bottom>
      <Page_Tabs
        :tabs="tabOptions"
        :initial-tab="currentTab"
        class="px-6 pt-4"
        no-border
        @select="onTabSelect"
      />
    </template>
  </PageHeader>

  <div class="flex-1 overflow-auto min-h-0">
    <!-- Patients Tab -->
    <div v-if="currentTab === 'Patients'" class="p-6">
      <div class="bg-white rounded border border-gray-200">
        <CareManagement_Patients_List :initial-patient-id="selectedId ? Number(selectedId) : null" />
      </div>
    </div>

    <!-- My Tasks Tab (IHCI/tenant 8 only) -->
    <div v-if="currentTab === 'My Tasks' && current_tenant_slug === 'ihci'" class="p-6">
      <div class="bg-white rounded border border-gray-200">
        <CareManagement_MyTasks />
      </div>
    </div>

    <!-- My Calendar Tab -->
    <div v-if="currentTab === 'My Calendar'" class="p-6">
      <div class="bg-white rounded border border-gray-200">
        <CareManagement_MyCalendar />
      </div>
    </div>

    <!-- Overview Tab: SubNav + content -->
    <div v-if="currentTab === 'Overview'" class="flex">
      <div class="cm-subnav">
        <button
          v-for="sub in overviewSubPages"
          :key="sub.key"
          class="cm-subnav-btn"
          :class="{ active: overviewSubPage === sub.key }"
          @click="selectOverviewSub(sub.key)"
        >{{ sub.label }}</button>
      </div>
      <div class="flex-1 min-w-0">
        <CareManagement_Operations_IHCI v-if="overviewSubPage === 'operations' && current_tenant_slug === 'ihci'" />
        <CareManagement_DailyOpsMatrix v-else-if="overviewSubPage === 'operations'" />
        <CareManagement_DischargeWaterfall v-if="overviewSubPage === 'discharge_waterfall' && waterfallEnabled" />
        <CareManagement_Pipeline v-if="overviewSubPage === 'pipeline'" />
        <CareManagement_EpisodeGrouper v-if="overviewSubPage === 'episode_grouper'" />
      </div>
    </div>

    <!-- Assignments Tab: SubNav + content -->
    <div v-if="currentTab === 'Assignments'" class="flex">
      <div class="cm-subnav">
        <button
          v-for="sub in assignmentSubPages"
          :key="sub.key"
          class="cm-subnav-btn"
          :class="{ active: assignmentSubPage === sub.key }"
          @click="selectAssignmentSub(sub.key)"
        >
          <span>{{ sub.label }}</span>
          <span v-if="sub.badge" class="cm-count-pill warn">{{ sub.badge }}</span>
        </button>
      </div>
      <div class="flex-1 min-w-0">
        <TeamAssignments v-if="assignmentSubPage === 'team'" />
        <CaseloadPanel v-if="assignmentSubPage === 'caseloads'" />
        <AssignmentHealth v-if="assignmentSubPage === 'unassigned'" @health-loaded="onHealthLoaded" />
        <ProgramAssignments v-if="assignmentSubPage === 'programs'" />
        <BulkReassign v-if="assignmentSubPage === 'bulk_reassign'" />
      </div>
    </div>

    <!-- AI Risk Score Tab -->
    <div v-if="currentTab === 'AI Risk Score'" class="p-6">
      <CareManagement_AIRiskScore />
    </div>

    <!-- Search & Enroll Tab -->
    <div v-if="currentTab === 'Search & Enroll'" class="p-6">
      <div class="bg-white rounded border border-gray-200">
        <CareManagement_SearchEnroll />
      </div>
    </div>

    <!-- Registry Tab. Full-height, self-scrolling: the p-6 wrapper + a
         viewport-height child would stack padding on top of the height and
         overflow, so the Registry owns its own box (h-full) like Provider
         Messaging does. -->
    <div v-if="currentTab === 'Registry'" class="h-full bg-white rounded border border-gray-200">
      <CareManagement_Registry />
    </div>

    <!-- Data Integration Tab -->
    <div v-if="currentTab === 'Data Integration'" class="p-6">
      <div class="bg-white rounded border border-gray-200">
        <PHM_DataIntegration />
      </div>
    </div>

    <!-- QA Tab: per-care-manager call quality scorecards. Manager and above. -->
    <div v-if="currentTab === 'QA'" class="h-full bg-white rounded border border-gray-200">
      <CareManagement_QA />
    </div>

    <!-- Provider Messaging Tab -->
    <div v-if="currentTab === 'Provider Messaging'" class="h-full bg-white rounded border border-gray-200">
      <ProviderMessagingPage />
    </div>

    <!-- Utilization Mgmt Tab: read-only admission census + human-queued rounds
         + decision ledger. Superuser-only until go-live. Full-height,
         self-scrolling like Registry. -->
    <div v-if="currentTab === 'Utilization Mgmt'" class="h-full bg-white rounded border border-gray-200">
      <CareManagement_UM />
    </div>
  </div>

  </div>
</template>

<script>
import '@/assets/cm-tokens.css';
import { mapGetters } from 'vuex'
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';
import CareManagement_Patients_List from '@/components/Care_Management/CareManagement_Patients_List.vue';
import CareManagement_MyTasks from '@/components/Care_Management/CareManagement_MyTasks.vue';
import CareManagement_Operations_IHCI from '@/components/Care_Management/CareManagement_Operations_IHCI.vue';
import CareManagement_DailyOpsMatrix from '@/components/Care_Management/CareManagement_DailyOpsMatrix.vue';
import CareManagement_DischargeWaterfall from '@/components/Care_Management/CareManagement_DischargeWaterfall.vue';
import CareManagement_Pipeline from '@/components/Care_Management/CareManagement_Pipeline.vue';
import CareManagement_EpisodeGrouper from '@/components/Care_Management/CareManagement_EpisodeGrouper.vue';

// Tenants with a Discharge Waterfall adapter DA + worklist configured. MUST stay
// in step with WATERFALL_WORKLIST_BY_TENANT in discharge_waterfall_service.py —
// the backend raises for an unconfigured tenant rather than falling back to
// another tenant's data, so listing one here without the other is a hard error.
const WATERFALL_TENANTS = ['ihci', 'vytalize'];
import CareManagement_AIRiskScore from '@/components/Care_Management/CareManagement_AIRiskScore.vue';
import CareManagement_MyCalendar from '@/components/Care_Management/CareManagement_MyCalendar.vue';
import CareManagement_SearchEnroll from '@/components/Care_Management/CareManagement_SearchEnroll.vue';
import CareManagement_Registry from '@/components/Care_Management/CareManagement_Registry.vue';
import PHM_DataIntegration from '@/components/PHM/PHM_DataIntegration.vue';
import TeamAssignments from '@/components/Care_Management/TeamAssignments.vue';
import CaseloadPanel from '@/components/Care_Management/CaseloadPanel.vue';
import AssignmentHealth from '@/components/Care_Management/AssignmentHealth.vue';
import ProgramAssignments from '@/components/Care_Management/ProgramAssignments.vue';
import BulkReassign from '@/components/Care_Management/BulkReassign.vue';
import ProviderMessagingPage from '@/components/Care_Management/ProviderMessagingPage.vue';
import CareManagement_QA from '@/components/Care_Management/CareManagement_QA.vue';
import CareManagement_UM from '@/components/Care_Management/CareManagement_UM.vue';

import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';

export default {
  name: 'CareManagementOverview',
  components: {
    CareManagement_Patients_List,
    CareManagement_MyTasks,
    CareManagement_Operations_IHCI,
    CareManagement_DailyOpsMatrix,
    CareManagement_DischargeWaterfall,
    CareManagement_Pipeline,
    CareManagement_EpisodeGrouper,
    CareManagement_AIRiskScore,
    CareManagement_MyCalendar,
    CareManagement_SearchEnroll,
    CareManagement_Registry,
    PHM_DataIntegration,
    TeamAssignments,
    CaseloadPanel,
    AssignmentHealth,
    ProgramAssignments,
    BulkReassign,
    ProviderMessagingPage,
    CareManagement_QA,
    CareManagement_UM,
    PageHeader,
    Page_Tabs,
  },
  props: {
    activeTab: { type: String, default: 'Patients' },
    selectedId: { type: [String, Number], default: null },
  },
  data() {
    return {
      currentTab: this.activeTab,
      overviewSubPage: 'operations',
      assignmentSubPage: 'team',
      assignmentSubPages: [
        { key: 'team', label: 'Team Assignments' },
        { key: 'caseloads', label: 'Caseloads' },
        { key: 'unassigned', label: 'Unassigned Tasks', badge: null },
        { key: 'programs', label: 'Program Assignments' },
        { key: 'bulk_reassign', label: 'Bulk Reassign' },
      ],
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug', 'isManagerViewer', 'isTenantAdmin']),
    waterfallEnabled() { return WATERFALL_TENANTS.includes(this.current_tenant_slug); },
    overviewSubPages() {
      // Daily Operations: for Vytalize this is the merged role×tier matrix view
      // (the old flat "Daily Operations" + "Daily Ops Overview" were combined
      // into one — Chris Weigert, 2026-07-20). IHCI keeps its own operations view.
      const subs = [
        { key: 'operations', label: 'Daily Operations' },
      ];
      // Discharge Waterfall: the post-discharge funnel report (Nesha/Rachel
      // 2026-07-22 spec). Per-tenant, gated on having a waterfall worklist +
      // adapter DA configured — see WATERFALL_WORKLIST_BY_TENANT in
      // discharge_waterfall_service.py. Keep this list in step with that dict:
      // a tenant listed here but not there gets a hard 500 from the endpoint.
      if (WATERFALL_TENANTS.includes(this.current_tenant_slug)) {
        subs.push({ key: 'discharge_waterfall', label: 'Discharge Waterfall' });
      }
      subs.push({ key: 'pipeline', label: 'Pipeline' });
      // Episode Grouper: algorithmic ADT_EPISODES vs the nightly LLM encounter
      // resolver. Open to every CM role — the people who adjudicate these
      // disagreements (the operational leads who report the episode-grouping
      // bugs) hold `standard`, and gating this manager+ locked out its intended
      // readers. Read-only; no PHI beyond the MBI already on every CM screen.
      // Backend access_roles on EpisodeGrouperComparisonView is the real gate.
      subs.push({ key: 'episode_grouper', label: 'Episode Grouper' });
      // 'program_effect' was a scratch table of completed opportunities with the
      // care-graph loop flags. It is now the episode drilldown INSIDE Discharge
      // Waterfall (click any statistic), sourced from the same DA as the
      // aggregates so the list and the numbers cannot disagree.
      return subs;
    },
    tabOptions() {
      const tabs = [
        { label: 'Overview', icon: ['fas', 'chart-bar'] },
        { label: 'Patients', icon: ['fas', 'users'] },
      ];
      // My Tasks (cross-patient task-grain to-do list) is IHCI-only for now
      // (tenant 8). Gated by slug — same pattern as the drawer 'insights' tab.
      if (this.current_tenant_slug === 'ihci') {
        tabs.push({ label: 'My Tasks', icon: ['fas', 'list-check'] });
      }
      tabs.push(
        { label: 'My Calendar', icon: ['fas', 'calendar-check'] },
        { label: 'Assignments', icon: ['fas', 'users-cog'] },
        { label: 'Search & Enroll', icon: ['fas', 'search'] },
        { label: 'Registry', icon: ['fas', 'layer-group'] },
      );
      if (this.current_user?.is_superuser) {
        tabs.push(
          { label: 'AI Risk Score', icon: ['fas', 'brain'] },
          { label: 'Data Integration', icon: ['fas', 'table'] },
          // Utilization Mgmt: superuser-only until go-live. Read-only census +
          // human-queued rounds — no opportunity/task machinery behind it, so
          // widening this gate is the only launch act.
          { label: 'Utilization Mgmt', icon: ['fas', 'hospital'] },
        )
      }
      // QA scorecards are a MANAGEMENT surface: they rank named care managers
      // on call quality. Manager, tenant admin, or superuser only — never the
      // care managers being scored.
      if (this.isManagerViewer || this.isTenantAdmin || this.current_user?.is_superuser) {
        tabs.push({ label: 'QA', icon: ['fas', 'clipboard-check'] });
      }
      if (canAccessFeature(FEATURES.MODULE_PROVIDER_MESSAGING, { superuserOverride: false })) {
        tabs.push({ label: 'Provider Messaging', icon: ['fas', 'envelope'] })
      }
      return tabs
    },
  },
  watch: {
    activeTab(val) {
      this.currentTab = this.guardTab(val);
    },
    // Deep-linking / back-forward: keep the subtab in sync with the hash.
    '$route.hash'(hash) {
      this.applyHashSubPage(hash);
    },
  },
  created() {
    // Direct URL hit on /caremanagement/tasks by a non-IHCI tenant would render
    // a blank body (tab gated below). Bounce them to Patients and fix the URL.
    // NOTE: this guard lives here (not in data()) because methods/getters are
    // not yet bound to `this` when data() runs — calling guardTab() from data()
    // throws "guardTab is not a function".
    this.currentTab = this.guardTab(this.activeTab);
    if (this.activeTab === 'My Tasks' && this.current_tenant_slug !== 'ihci') {
      this.$router.replace('/caremanagement/patients').catch(() => {});
    }
    // Restore the active subtab from the URL hash (#operations, #team, ...).
    // The parent tab lives in the path; the subtab lives in the hash. An
    // unknown/ungated hash falls through to the group's default.
    this.applyHashSubPage(this.$route.hash);
  },
  methods: {
    // Strip the leading '#' and return the subtab key, or '' if none.
    _hashKey(hash) {
      return (hash || '').replace(/^#/, '');
    },
    // Point the subtab state at the hash key when it's valid for the current
    // tab; otherwise leave the default in place.
    applyHashSubPage(hash) {
      const key = this._hashKey(hash);
      if (!key) return;
      if (this.currentTab === 'Overview'
          && this.overviewSubPages.some(s => s.key === key)) {
        this.overviewSubPage = key;
      } else if (this.currentTab === 'Assignments'
          && this.assignmentSubPages.some(s => s.key === key)) {
        this.assignmentSubPage = key;
      }
    },
    selectOverviewSub(key) {
      this.overviewSubPage = key;
      this._setHash(key);
    },
    selectAssignmentSub(key) {
      this.assignmentSubPage = key;
      this._setHash(key);
    },
    _setHash(key) {
      if (this._hashKey(this.$route.hash) === key) return;
      this.$router.replace({ hash: `#${key}` }).catch(() => {});
    },
    // My Tasks is IHCI-only; anyone else falls back to Patients.
    guardTab(tab) {
      if (tab === 'My Tasks' && this.current_tenant_slug !== 'ihci') {
        return 'Patients';
      }
      return tab;
    },
    onTabSelect(tab) {
      this.currentTab = tab;
      if (tab === 'Overview') {
        // Carry the active subtab in the hash so a reload lands on the same view.
        this.$router.replace({ path: '/caremanagement/operations', hash: `#${this.overviewSubPage}` }).catch(() => {});
      } else if (tab === 'My Calendar') {
        this.$router.replace('/caremanagement/calendar').catch(() => {});
      } else if (tab === 'Patients') {
        this.$router.replace('/caremanagement/patients').catch(() => {});
      } else if (tab === 'My Tasks') {
        this.$router.replace('/caremanagement/tasks').catch(() => {});
      } else if (tab === 'Assignments') {
        this.$router.replace({ path: '/caremanagement/assignments', hash: `#${this.assignmentSubPage}` }).catch(() => {});
      } else if (tab === 'AI Risk Score') {
        this.$router.replace('/caremanagement/risk').catch(() => {});
      } else if (tab === 'Search & Enroll') {
        this.$router.replace('/caremanagement/search-enroll').catch(() => {});
      } else if (tab === 'Registry') {
        this.$router.replace('/caremanagement/registry').catch(() => {});
      } else if (tab === 'Data Integration') {
        this.$router.replace('/caremanagement/data-integration').catch(() => {});
      } else if (tab === 'Provider Messaging') {
        this.$router.replace('/caremanagement/provider-messaging').catch(() => {});
      } else if (tab === 'Utilization Mgmt') {
        this.$router.replace('/caremanagement/utilization-management').catch(() => {});
      }
    },
    onHealthLoaded(data) {
      const sub = this.assignmentSubPages.find(s => s.key === 'unassigned');
      if (sub) sub.badge = data?.total_unassigned || null;
    },
  },
};
</script>
